import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import type { TypedUseSelectorHook } from 'react-redux';
import type { RootState, AppDispatch } from './';

export const useAppDispatch: () => AppDispatch = useDispatch;
const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useAppSelectorShallow = <T>(
  selector: (state: RootState) => T
): T => {
  return useAppSelector(selector, shallowEqual);
};

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

// Simplified MemoizedSelector type
type MemoizedSelector<Result> = {
  (state: RootState): Result;
  clearCache: () => void;
  resultsCount: () => number;
  resetResultsCount: () => void;
  resetDependencyRecomputations: () => void;
  resetRecomputations: () => void;
  dependencyRecomputations: () => number;
  recomputations: () => number;
  lastResult: () => Result | undefined;
  memoizedResultFunc: (...args: unknown[]) => Result;
  argsMemoize: ((...args: unknown[]) => unknown) | undefined;
  memoize:
    | ((fn: (...args: unknown[]) => unknown) => (...args: unknown[]) => unknown)
    | undefined;
};

type ExtractReturnTypes<
  Selectors extends ReadonlyArray<(state: RootState) => unknown>
> = {
  [K in keyof Selectors]: Selectors[K] extends (state: RootState) => infer R
    ? R
    : never;
};

export const createAppSelector = <
  Selectors extends ReadonlyArray<(state: RootState) => unknown>,
  Result
>(
  inputSelectors: [...Selectors],
  resultFunc: (...args: ExtractReturnTypes<Selectors>) => Result
): MemoizedSelector<Result> => {
  let lastInputResults: unknown[] | undefined;
  let lastResult: Result | undefined;
  let resultsCount = 0;
  let dependencyRecomputations = 0;
  let recomputations = 0;

  const clearCache = () => {
    lastInputResults = undefined;
    lastResult = undefined;
    resultsCount = 0;
    dependencyRecomputations = 0;
    recomputations = 0;
  };

  const resetResultsCount = () => {
    resultsCount = 0;
  };

  const resetDependencyRecomputations = () => {
    dependencyRecomputations = 0;
  };

  const resetRecomputations = () => {
    recomputations = 0;
  };

  const selector = (state: RootState): Result => {
    const inputResults = inputSelectors.map((selector) => selector(state));

    const inputsChanged =
      !lastInputResults ||
      inputResults.length !== lastInputResults.length ||
      inputResults.some(
        (result, index) => result !== lastInputResults?.[index]
      );

    if (inputsChanged) {
      dependencyRecomputations++;
    }

    // If inputs haven't changed, check if we can return cached result
    if (!inputsChanged && lastResult !== undefined) {
      // Even if state reference changed, if inputs are the same,
      // check if result is shallow equal to avoid unnecessary re-renders
      if (isObject(lastResult)) {
        // For objects, we need to recompute to check shallow equality
        const newResult = resultFunc(
          ...(inputResults as unknown as ExtractReturnTypes<Selectors>)
        );
        recomputations++;

        if (isObject(newResult) && shallowEqual(newResult, lastResult)) {
          // Results are shallow equal, return cached result
          resultsCount++;
          return lastResult;
        }

        // Results differ, update cache
        lastResult = newResult;
        resultsCount++;
        return newResult;
      } else {
        // For primitives, if inputs haven't changed, result should be the same
        resultsCount++;
        return lastResult;
      }
    }

    // Inputs changed or no cached result, recompute
    if (inputsChanged || lastResult === undefined) {
      const newResult = resultFunc(
        ...(inputResults as unknown as ExtractReturnTypes<Selectors>)
      );
      recomputations++;

      // Check shallow equality for objects even when inputs changed
      // (in case input selectors return new references but same values)
      if (
        lastResult !== undefined &&
        isObject(newResult) &&
        isObject(lastResult)
      ) {
        if (shallowEqual(newResult, lastResult)) {
          lastInputResults = inputResults;
          resultsCount++;
          return lastResult;
        }
      }

      lastInputResults = inputResults;
      lastResult = newResult;
      resultsCount++;
      return newResult;
    }

    resultsCount++;
    return lastResult as Result;
  };

  // Attach methods to the selector function
  selector.clearCache = clearCache;
  selector.resultsCount = () => resultsCount;
  selector.resetResultsCount = resetResultsCount;
  selector.resetDependencyRecomputations = resetDependencyRecomputations;
  selector.resetRecomputations = resetRecomputations;
  selector.dependencyRecomputations = () => dependencyRecomputations;
  selector.recomputations = () => recomputations;
  selector.lastResult = () => lastResult;
  selector.memoizedResultFunc = resultFunc as (...args: unknown[]) => Result;
  selector.argsMemoize = undefined as
    | ((...args: unknown[]) => unknown)
    | undefined;
  selector.memoize = undefined as
    | ((fn: (...args: unknown[]) => unknown) => (...args: unknown[]) => unknown)
    | undefined;

  return selector as MemoizedSelector<Result>;
};
